import { EvaSeriesKind } from "constants/charts.enums";
import { DEFAULT_LINE_CHART_OPTIONS } from "entities/charts";
import { TYPEWELL_Z_INDEX } from "entities/charts/eva/translators/constants";
import { ICheckedForecast } from "store/features";
import { TypeWellSeriesData } from "types/charts/typeWellSeries";

import {
  CrossPlotNormalizationSettings,
  FIELD_TO_WELLDATA_MAP,
  WellDataUnitConversions,
  calculateNormalizationMultiplier,
  canNormalizeTypeWell,
  getCrossPlotFieldValue,
  getTypeWellDisplayName,
  isNormalizableField,
  shouldSkipZeroValue
} from "./typeWellHelpers";

const CROSS_PLOT_TYPEWELL_SYMBOL_SCALE = 22;

export interface CrossPlotSeriesParams {
  typeWells: ICheckedForecast[];
  xProperty: string;
  yProperty: string;
  performanceData: Record<string, Record<string, number | null>> | undefined;
  normalization?: CrossPlotNormalizationSettings;
  unitConversions?: WellDataUnitConversions;
}

export interface CrossPlotSeriesResult {
  series: TypeWellSeriesData;
  missingCount: number;
  missingDataCount: number;
}

/**
 * Build horizontal markLine series for cross plot when X-axis is a date or header field.
 * Each type well gets a dashed horizontal line at its Y performance value.
 * Note: When Y is also a date or header field, markLines cannot be rendered (returns empty series).
 */
function buildCrossPlotMarkLineSeries(
  params: Omit<CrossPlotSeriesParams, "xProperty">
): CrossPlotSeriesResult {
  const { typeWells, yProperty, performanceData, normalization, unitConversions } =
    params;

  // When Y is a date or header field, we can't render meaningful markLines
  // (date Y values don't make visual sense, and type wells don't have header data)
  if (yProperty.startsWith("Dates.") || yProperty.startsWith("Header.")) {
    return { series: [], missingCount: 0, missingDataCount: typeWells.length };
  }

  const series: TypeWellSeriesData = [];
  let missingCount = 0;
  let missingDataCount = 0;

  // Check if Y field should be normalized
  const shouldNormalizeY =
    normalization?.enabled &&
    normalization?.normalizeTypeWell &&
    isNormalizableField(yProperty);

  for (const tw of typeWells) {
    const perfData = performanceData?.[tw.id];

    let yValue = getCrossPlotFieldValue(tw, yProperty, perfData, unitConversions);
    if (yValue == null || typeof yValue !== "number") {
      missingDataCount++;
      continue;
    }

    // Skip zero values only if the product doesn't exist in the type well
    if (shouldSkipZeroValue(tw, yProperty, yValue)) {
      missingDataCount++;
      continue;
    }

    if (shouldNormalizeY && normalization) {
      const { canNormalize, wellValue } = canNormalizeTypeWell(tw, normalization);

      if (!canNormalize) {
        missingCount++;
        continue;
      }

      // Apply normalization if we have a valid well value
      if (wellValue != null) {
        const normalizeMultiplier = calculateNormalizationMultiplier(
          wellValue,
          normalization
        );
        yValue = yValue * normalizeMultiplier;
      }
    }

    const typeWellName = getTypeWellDisplayName(tw);

    // Create a scatter series with markLine for horizontal line visualization
    series.push({
      ...DEFAULT_LINE_CHART_OPTIONS,
      name: typeWellName,
      id: `typewell-perf-${tw.id}`,
      originalId: tw.id,
      type: "scatter",
      kind: EvaSeriesKind.TypeWell,
      data: [], // Empty data - the line is drawn via markLine
      symbol: "none",
      z: TYPEWELL_Z_INDEX,
      markLine: {
        silent: false,
        symbol: "none",
        lineStyle: {
          color: tw.color ?? "#000000",
          width: tw.thickness,
          type: "dashed"
        },
        emphasis: {
          lineStyle: {
            width: (tw.thickness ?? 2) + 2,
            opacity: 1
          }
        },
        label: {
          show: false
        },
        data: [{ yAxis: yValue, name: typeWellName }]
      }
    } as TypeWellSeriesData[0]);
  }

  return { series, missingCount, missingDataCount };
}

/**
 * Build scatter series for cross plot type well performance points.
 * - When X-axis is a date or header field: Returns horizontal markLine series
 * - Otherwise: Returns scatter points at (X, Y) coordinates
 * Supports normalization when enabled.
 * Returns both the series and a count of type wells missing normalization data.
 */
export function buildCrossPlotSeries(
  params: CrossPlotSeriesParams
): CrossPlotSeriesResult {
  const {
    typeWells,
    xProperty,
    yProperty,
    performanceData,
    normalization,
    unitConversions
  } = params;

  // Check if X-axis is a date or header field - if so, use horizontal markLines instead of scatter points
  // Type wells don't have header field values, so we show horizontal lines at Y value
  const xIsDateField = xProperty.startsWith("Dates.");
  const xIsHeaderField = xProperty.startsWith("Header.");
  if (xIsDateField || xIsHeaderField) {
    return buildCrossPlotMarkLineSeries({
      typeWells,
      yProperty,
      performanceData,
      normalization,
      unitConversions
    });
  }

  // Check if we need API data (field is not a Dates field and not in wellData)
  // Note: xIsDateField is already checked above, so xIsDateField is false here
  const yIsDateField = yProperty.startsWith("Dates.");
  const xHasWellDataFallback = !!FIELD_TO_WELLDATA_MAP[xProperty];
  const yHasWellDataFallback = !!FIELD_TO_WELLDATA_MAP[yProperty];

  const needsApiData = !xHasWellDataFallback || (!yIsDateField && !yHasWellDataFallback);
  if (needsApiData && !performanceData)
    return { series: [], missingCount: 0, missingDataCount: 0 };

  // Determine which fields should be normalized
  const shouldNormalizeX =
    normalization?.enabled &&
    normalization?.normalizeTypeWell &&
    isNormalizableField(xProperty);
  const shouldNormalizeY =
    normalization?.enabled &&
    normalization?.normalizeTypeWell &&
    isNormalizableField(yProperty);

  const needsNormalization = shouldNormalizeX || shouldNormalizeY;

  const series: TypeWellSeriesData = [];
  let missingCount = 0;
  let missingDataCount = 0;

  for (const tw of typeWells) {
    const perfData = performanceData?.[tw.id];

    let xValue = getCrossPlotFieldValue(tw, xProperty, perfData, unitConversions);
    let yValue = getCrossPlotFieldValue(tw, yProperty, perfData, unitConversions);

    if (xValue == null || yValue == null) {
      missingDataCount++;
      continue;
    }

    // Skip zero values only if the product doesn't exist in the type well
    if (
      shouldSkipZeroValue(tw, xProperty, xValue) ||
      shouldSkipZeroValue(tw, yProperty, yValue)
    ) {
      missingDataCount++;
      continue;
    }

    // Check if normalization is required but well data is missing
    if (needsNormalization && normalization) {
      const { canNormalize, wellValue } = canNormalizeTypeWell(tw, normalization);

      if (!canNormalize) {
        missingCount++;
        continue;
      }

      if (wellValue != null) {
        const normalizeMultiplier = calculateNormalizationMultiplier(
          wellValue,
          normalization
        );

        if (shouldNormalizeX && typeof xValue === "number") {
          xValue = xValue * normalizeMultiplier;
        }
        if (shouldNormalizeY && typeof yValue === "number") {
          yValue = yValue * normalizeMultiplier;
        }
      }
    }

    const typeWellName = getTypeWellDisplayName(tw);
    series.push({
      ...DEFAULT_LINE_CHART_OPTIONS,
      name: typeWellName,
      id: `typewell-perf-${tw.id}`,
      originalId: tw.id,
      type: "scatter",
      kind: EvaSeriesKind.TypeWell,
      symbol: "diamond",
      symbolSize: (tw.thickness ?? 2) + CROSS_PLOT_TYPEWELL_SYMBOL_SCALE,
      itemStyle: {
        color: tw.color ?? "#000000",
        opacity: 1
      },
      emphasis: {
        scale: 1.2,
        itemStyle: {
          opacity: 1
        }
      },
      blur: {
        itemStyle: {
          opacity: 0.8
        }
      },
      z: TYPEWELL_Z_INDEX,
      data: [[xValue, yValue]]
    } as TypeWellSeriesData[0]);
  }

  return { series, missingCount, missingDataCount };
}

/**
 * Extract fields needed for cross plot API request.
 * Excludes date fields which are handled locally using arps start date.
 */
export function extractCrossPlotFields(
  xProperty: string | undefined,
  yProperty: string | undefined
): string[] {
  const fields: string[] = [];
  if (xProperty && !xProperty.startsWith("Dates.")) fields.push(xProperty);
  if (yProperty && yProperty !== xProperty && !yProperty.startsWith("Dates.")) {
    fields.push(yProperty);
  }
  return fields;
}
